import type { ReactNode } from 'react';
import { FormSection, IconButton } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { ProcessActivityPatientContactDetails } from '@common/interfaces/patientContactDetails';
import { useProcessActivityEditContext } from '@frontend/utils';
import { usePatientEpisodeCategoryContext } from '@frontend/utils/hooks/usePatientEpisodeCategoryContext';
import { EmailAddressFormGroup } from '../EmailAddressFormGroup';
import { PhoneNumberFormGroup } from '../PhoneNumberFormGroup';
import { AddressAddEditFormGroup } from './AddressAddEditFormGroup';
import { AddressFormGroupFormParagraph } from './PatientContactDetailsRead';

export type PatientContactDetailsFormSectionProps = Readonly<{
    alert?: ReactNode;
    showAddress?: boolean;
}>;

export function PatientContactDetailsFormSection({ alert, showAddress = true }: PatientContactDetailsFormSectionProps) {
    const { patient } = usePatientEpisodeCategoryContext();
    const { data, errors, onChange, processActivityId } =
        useProcessActivityEditContext<ProcessActivityPatientContactDetails>();
    const [isPatientAddressEdit, setIsPatientAddressEdit] = useState(false);

    const isAddressComplete = Boolean(
        data.patient.street && data.patient.zip && data.patient.city && data.patient.houseNumber
    );

    const isAddressPartiallyComplete = Boolean(
        data.patient.street ?? data.patient.zip ?? data.patient.city ?? data.patient.houseNumber
    );

    const [addressHasBeenFilledOnce, setAddressHasBeenFilledOnce] = useState(!showAddress || false);

    const changeAddressAction = !isPatientAddressEdit ? (
        <IconButton
            icon="edit-light"
            labelText="edit"
            onClick={() => setIsPatientAddressEdit(true)}
            size="small"
        />
    ) : undefined;

    const isCountryNLOrEmpty = patient.country === 'NL' || !patient.country;

    useEffect(() => {
        if (!processActivityId || (!isAddressPartiallyComplete && !addressHasBeenFilledOnce)) {
            onChange(fillDefaultPatientContactDetails(data));
            setAddressHasBeenFilledOnce(true);
        }
    }, [processActivityId, isAddressPartiallyComplete, addressHasBeenFilledOnce]);

    useEffect(() => {
        if (!isAddressComplete) {
            setIsPatientAddressEdit(true);
        }
    }, [isAddressComplete]);

    useEffect(() => {
        if (isAddressPartiallyComplete) {
            setAddressHasBeenFilledOnce(true);
        }
    }, [isAddressPartiallyComplete]);

    return (
        <FormSection
            action={changeAddressAction}
            title="Contactgegevens patiënt"
        >
            {showAddress && (
                <>
                    {isPatientAddressEdit && alert}

                    {isAddressComplete && !isPatientAddressEdit ? (
                        <AddressFormGroupFormParagraph address={data.patient} />
                    ) : (
                        <AddressAddEditFormGroup
                            addressFieldKey="patient"
                            errors={errors}
                            isRequired={true}
                        />
                    )}
                </>
            )}

            <EmailAddressFormGroup
                data={data}
                errors={errors}
                onChange={onChange}
            />

            <PhoneNumberFormGroup
                data={{
                    homePhoneNumber: data.patient.homePhoneNumber,
                    mobileNumber: data.patient.mobileNumber,
                }}
                errors={errors}
                onChange={(patientPhoneData) =>
                    onChange(
                        {
                            patient: {
                                ...data.patient,
                                ...patientPhoneData,
                            },
                        },
                        'patient'
                    )
                }
            />
        </FormSection>
    );

    function fillDefaultPatientContactDetails(contactData: ProcessActivityPatientContactDetails) {
        const address = isCountryNLOrEmpty
            ? {
                  city: patient.city ?? null,
                  country: 'NL',
                  houseLetter: patient.houseLetter ?? null,
                  houseNumber: patient.houseNumber ?? null,
                  houseNumberSuffix: patient.houseNumberSuffix ?? null,
                  street: patient.street ?? null,
                  zip: patient.zip ?? null,
              }
            : {
                  city: null,
                  country: 'NL',
                  houseLetter: null,
                  houseNumber: null,
                  houseNumberSuffix: null,
                  street: null,
                  zip: null,
              };

        return {
            ...contactData,
            patient: {
                ...contactData.patient,
                ...address,
                emailAddress: patient.emailAddress ?? null,
                homePhoneNumber: patient.homePhoneNumber ?? null,
                mobileNumber: patient.mobileNumber ?? null,
            },
        };
    }
}
